import {Injectable, inject} from '@angular/core';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {ApiEndpoints} from '../../_constants/api-endpoints';
import {Observable, of, timer} from 'rxjs';
import {DeviceDetails, MfaUserDetails} from '../../_data/mfa/mfa-user-details';
import {faAt} from '@fortawesome/free-solid-svg-icons/faAt';
import {IconProp} from '@fortawesome/fontawesome-svg-core';
import {faComments} from '@fortawesome/free-solid-svg-icons/faComments';
import {faPhone} from '@fortawesome/free-solid-svg-icons/faPhone';
import {
  MfaActivationCode,
  MfaFinishPairingOrAuth,
  MfaFinishPairingOrAuthResponse,
  MfaPairing,
  MfaPairingResponse,
  MfaUpdatePrimaryDevice,
  MfaUserDeviceId,
  MfaUserSessionId,
  UserMfaDetails,
  UserMfaDetailsActivationCode
} from '../../_data/mfa/mfa-pairing';
import {catchError, filter, first, map, mergeMap, take, takeUntil, tap} from 'rxjs/operators';
import {PingPairingStatus} from '../../_constants/types';
import {faMobile} from '@fortawesome/free-solid-svg-icons/faMobile';
import {MfaAuthenticationResponse} from '../../_data/mfa/mfa-authentication';
import {JwtToken} from '../../_data/admin/jwt-token';
import {JWT_TYPE_HEADER} from '../../_constants/constants';

@Injectable({
  providedIn: 'root'
})
export class MfaService {
  private readonly http = inject(HttpClient);
  private readonly apiEndpoints = inject(ApiEndpoints);


  getTypeIcon(deviceDetails: DeviceDetails): IconProp {
    if(deviceDetails?.type === 'Email') {
      return faAt;
    } else if(deviceDetails?.type === 'SMS') {
      return faComments;
    } else if(deviceDetails?.type === 'Voice') {
      return faPhone;
    } else if(deviceDetails?.type === 'Android' || deviceDetails?.type === 'iPhone') {
      return faMobile;
    }
    return null;
  }

  getDescriptionI18nKey(deviceDetails: DeviceDetails): string {
    if(deviceDetails?.type === 'Email') {
      return 'content.mfa-device-descriptions.email';
    } else if(deviceDetails?.type === 'SMS') {
      return 'content.mfa-device-descriptions.sms';
    } else if(deviceDetails?.type === 'Voice') {
      return 'content.mfa-device-descriptions.voice';
    } else if(deviceDetails?.type === 'Android' || deviceDetails?.type === 'iPhone') {
      return 'content.mfa-device-descriptions.app';
    }
    return null;
  }

  getUserMfaDetails(userName: string, additionalHeaders = {}): Observable<MfaUserDetails> {
    console.info(`Getting mfa details for ${userName}`);
    return this.http.post<MfaUserDetails>(
      this.apiEndpoints.mfaUserDetails,
      {userName: userName},
      {headers: Object.assign(additionalHeaders, {[JWT_TYPE_HEADER]: 'auth'})}
    );
  }

  startAuthentication(mfaUserDeviceId: MfaUserDeviceId): Observable<MfaAuthenticationResponse> {
    console.info('Starting mfa authentication');
    return this.http.post<MfaAuthenticationResponse>(this.apiEndpoints.mfaStartOfflineAuth, mfaUserDeviceId, {headers: {[JWT_TYPE_HEADER]: 'auth'}});
  }

  startAuthOnline(mfaUserDeviceId: MfaUserDeviceId): Observable<MfaAuthenticationResponse> {
    console.info('Starting online authentication');
    return this.http.post<MfaAuthenticationResponse>(this.apiEndpoints.mfaAuthOnline, mfaUserDeviceId, {headers: {[JWT_TYPE_HEADER]: 'auth'}});
  }

  awaitOnlineAuth(mfaUserSessionId: MfaUserSessionId): Observable<JwtToken<any>> {
    console.debug('Checking online auth status');

    //start a timer to poll the status
    return timer(1000, 1000).pipe(
      //Switch over to the actual service check
      mergeMap(() =>
        this.http.post<JwtToken<any>>(
          this.apiEndpoints.mfaCheckAuthStatus, mfaUserSessionId,
          {headers: {[JWT_TYPE_HEADER]: 'auth', ignoreLoadingBar: ''}, observe: 'response'}
        ).pipe(catchError(() => of(null)))
      ),
      //Filter by status NOT being 202.  202 will allow the timer to re-run.  All other cases will fall through.
      filter((response: HttpResponse<any>) => response?.status !== 202),
      //Map it to the response body
      map(response => response?.body),
      first()
    ) as Observable<JwtToken<any>>;

  }

  finishAuthentication(mfaUserSessionOtp: MfaFinishPairingOrAuth, jwtToken: JwtToken<any>): Observable<JwtToken<MfaFinishPairingOrAuthResponse>> {
    console.info('Finishing mfa authentication');
    return this.http.post<JwtToken<MfaFinishPairingOrAuthResponse>>(
      this.apiEndpoints.mfaFinishOfflineAuth, mfaUserSessionOtp,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      {headers: {Authorization: `Bearer ${jwtToken.token}`}}
    ).pipe(catchError(() => of(null)));
  }

  startOfflinePairing(mfaPairing: MfaPairing): Observable<MfaPairingResponse> {
    console.info(`Starting mfa offline pairing`);
    return this.http.post<MfaPairingResponse>(this.apiEndpoints.mfaStartOfflinePairing, mfaPairing, {headers: {[JWT_TYPE_HEADER]: 'auth'}});
  }

  finishOfflinePairing(mfaFinishPairing: MfaFinishPairingOrAuth): Observable<MfaFinishPairingOrAuthResponse> {
    console.info(`Finishing offline pairing ${JSON.stringify(mfaFinishPairing)}`);
    return this.http.post<MfaFinishPairingOrAuthResponse>(this.apiEndpoints.mfaFinishOfflinePairing, mfaFinishPairing, {headers: {[JWT_TYPE_HEADER]: 'auth'}});
  }

  unpairDevice(mfaUnpairing: MfaUserDeviceId): Observable<MfaPairingResponse> {
    console.info(`Unpairing mfa device: ${JSON.stringify(mfaUnpairing)}`);
    return this.http.post<MfaPairingResponse>(`${this.apiEndpoints.mfaUnpairDevice}`, mfaUnpairing);
  }

  updatePrimaryDevice(mfaUpdatePrimaryDevice: MfaUpdatePrimaryDevice): Observable<MfaPairingResponse> {
    console.info(`Updating primary mfa device ${JSON.stringify(mfaUpdatePrimaryDevice)}`);
    return this.http.post<MfaPairingResponse>(this.apiEndpoints.mfaUpdatePrimaryDevice, mfaUpdatePrimaryDevice);
  }

  requestQrCode(userMFADetails: UserMfaDetails): Observable<MfaActivationCode> {
    console.info('Getting QR code to start pairing ping app');
    return this.http.post<MfaActivationCode>(this.apiEndpoints.mfaRequestActivationCode, {userMFADetails}, {headers: {[JWT_TYPE_HEADER]: 'auth'}});
  }

  awaitPaired(activationCode: string, userName: string, cancel$: Observable<void>): Observable<void> {
    console.info('Waiting for activationCode to be paired');

    //Start a timer that'll poll the endpoint.
    return timer(1000, 1000).pipe(
      //Check the status
      mergeMap(() =>
        this.http.post(
          this.apiEndpoints.mfaAppPairingStatus,
          {userName: userName, activationCode: activationCode} as UserMfaDetailsActivationCode,
          {headers: {ignoreLoadingBar: '', [JWT_TYPE_HEADER]: 'auth'}, responseType: 'text'}
        )
      ),
      //Cancel the whole thing if told to
      takeUntil(cancel$),
      //Only continue once the status is PAIRED
      filter((pairingStatus) => (pairingStatus as PingPairingStatus) === 'PAIRED'),
      //If it is PAIRED, then just let one through, don't need the timer to run anymore.
      take(1),

      //"Paired" happens when the code is taken, but the device doesn't show up until the user finishes the setup on their phone
      //So, poll the device details until one with a non-null appVersion is found
      mergeMap(() => timer(1000, 1000).pipe(
        //Check device details
        mergeMap(() => this.getUserMfaDetails(userName, {ignoreLoadingBar: ''})),
        //Cancel the whole thing if told to
        takeUntil(cancel$),
        //See if there's one that has a non-null appVersion
        filter(userDetails => !!userDetails?.userDetails?.devicesDetails?.find(device => !!device.appVersion)),
        take(1)
      )),

      tap(() => console.info('Ping app paired')),
      mergeMap(() => of(undefined))
    ) as Observable<void>;

  }

  toLabel(device: DeviceDetails) {
    return device?.displayID ?? device?.email ?? device?.phoneNumber ?? device?.type;
  }
}
